import { useState, useEffect } from 'react'
import {
  Puzzle, Plus, Edit3, Trash2, Search, Filter, Check, X, Clock,
  AlertCircle, Tag, Loader, Wrench, ChevronRight
} from 'lucide-react'
import type { Task, Competency } from '../engine/types'
import { listTasks, createTask, updateTask, deleteTask } from '../engine/TaskBuilder'
import { listCompetencies } from '../engine/CompetencyStudio'
import { useLang } from '../i18n/LangContext'

const statusConfig: Record<string, { bg: string; text: string; label: string }> = {
  draft:      { bg: 'rgba(90,94,120,0.12)', text: 'var(--color-text-muted)', label: 'Draft' },
  reviewing:  { bg: 'rgba(251,191,36,0.12)', text: 'var(--color-warning)', label: 'Reviewing' },
  approved:   { bg: 'rgba(96,165,250,0.12)', text: 'var(--color-info)', label: 'Approved' },
  published:  { bg: 'rgba(74,222,128,0.12)', text: 'var(--color-success)', label: 'Published' },
  deprecated: { bg: 'rgba(248,113,113,0.12)', text: 'var(--color-danger)', label: 'Deprecated' },
}

function getDifficultyColor(difficulty: string): { bg: string; text: string } {
  const level = parseInt(difficulty.replace('L', ''), 10)
  if (level <= 2) return { bg: 'rgba(74,222,128,0.12)', text: 'var(--color-success)' }
  if (level <= 4) return { bg: 'rgba(96,165,250,0.12)', text: 'var(--color-info)' }
  if (level <= 6) return { bg: 'rgba(251,191,36,0.12)', text: 'var(--color-warning)' }
  return { bg: 'rgba(248,113,113,0.12)', text: 'var(--color-danger)' }
}

interface TaskForm {
  title: string
  description: string
  difficulty: string
  competency_id: string
  rubric_id: string
  tools_allowed: string[]
  expected_steps: number
  status: string
}

const emptyTaskForm: TaskForm = {
  title: '',
  description: '',
  difficulty: 'L1',
  competency_id: '',
  rubric_id: '',
  tools_allowed: [],
  expected_steps: 1,
  status: 'draft',
}

const difficultyOptions = ['L1', 'L2', 'L3', 'L4', 'L5', 'L6', 'L7', 'L8']

export default function TaskBuilderPage() {
  const { t } = useLang()
  const [tasks, setTasks] = useState<Task[]>([])
  const [competencies, setCompetencies] = useState<Competency[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [filterCompetency, setFilterCompetency] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [form, setForm] = useState<TaskForm>(emptyTaskForm)
  const [toolInput, setToolInput] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    async function fetch() {
      try {
        setLoading(true)
        setError(null)
        const [taskRes, compRes] = await Promise.all([
          listTasks(),
          listCompetencies(),
        ])
        if (cancelled) return
        setTasks(taskRes)
        setCompetencies(compRes)
      } catch (err) {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Failed to load data')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    fetch()
    return () => { cancelled = true }
  }, [])

  const filtered = tasks.filter(t => {
    if (filterCompetency && t.competency_id !== filterCompetency) return false
    if (!searchQuery) return true
    const q = searchQuery.toLowerCase()
    return (
      t.title.toLowerCase().includes(q) ||
      t.description.toLowerCase().includes(q) ||
      t.difficulty.toLowerCase().includes(q)
    )
  })

  function getCompetencyName(id: string): string {
    const comp = competencies.find(c => c.id === id)
    return comp ? `${comp.name} (${comp.name_cn})` : id
  }

  function openCreateModal() {
    setEditingId(null)
    setForm(emptyTaskForm)
    setToolInput('')
    setSubmitError(null)
    setShowModal(true)
  }

  function openEditModal(task: Task) {
    setEditingId(task.id)
    setForm({
      title: task.title,
      description: task.description,
      difficulty: task.difficulty,
      competency_id: task.competency_id,
      rubric_id: task.rubric_id,
      tools_allowed: [...task.tools_allowed],
      expected_steps: task.expected_steps,
      status: task.status,
    })
    setToolInput('')
    setSubmitError(null)
    setShowModal(true)
  }

  function addTool() {
    const trimmed = toolInput.trim()
    if (trimmed && !form.tools_allowed.includes(trimmed)) {
      setForm(prev => ({ ...prev, tools_allowed: [...prev.tools_allowed, trimmed] }))
      setToolInput('')
    }
  }

  function removeTool(tool: string) {
    setForm(prev => ({ ...prev, tools_allowed: prev.tools_allowed.filter(t => t !== tool) }))
  }

  async function handleSubmit() {
    if (!form.title || !form.competency_id) {
      setSubmitError('Title and Competency are required')
      return
    }
    try {
      setSubmitting(true)
      setSubmitError(null)

      const payload: Partial<Task> = {
        title: form.title,
        description: form.description,
        difficulty: form.difficulty,
        competency_id: form.competency_id,
        rubric_id: form.rubric_id,
        tools_allowed: form.tools_allowed,
        expected_steps: form.expected_steps,
        status: form.status as Task['status'],
      }

      if (editingId) {
        await updateTask(editingId, payload)
      } else {
        await createTask(payload)
      }

      const data = await listTasks()
      setTasks(data)
      setShowModal(false)
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to save task')
    } finally {
      setSubmitting(false)
    }
  }

  async function handleDelete(id: string) {
    try {
      await deleteTask(id)
      setTasks(prev => prev.filter(t => t.id !== id))
      setConfirmDelete(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete task')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-3">
          <Loader size={32} className="animate-spin" style={{ color: 'var(--color-accent)' }} />
          <span style={{ color: 'var(--color-text-secondary)' }}>{t('common.loading')}</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-3 max-w-md text-center p-6 rounded-xl" style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)' }}>
          <AlertCircle size={32} style={{ color: 'var(--color-danger)' }} />
          <h2 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>{t('common.error')}</h2>
          <p style={{ color: 'var(--color-text-secondary)' }}>{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            style={{ backgroundColor: 'var(--color-accent)', color: '#fff' }}
          >
            {t('common.retry')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="container-page px-4 sm:px-6 lg:px-8 xl:px-12 py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>{t('task.title')}</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>{t('task.subtitle')}</p>
        </div>
        <button
          onClick={openCreateModal}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors"
          style={{ backgroundColor: 'var(--color-accent)', color: '#fff' }}
        >
          <Plus size={16} />
          {t('task.new')}
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-text-muted)' }} />
          <input
            type="text"
            placeholder={t('task.search')}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-lg text-sm outline-none transition-colors"
            style={{
              backgroundColor: 'var(--color-bg-card)',
              border: '1px solid var(--color-border)',
              color: 'var(--color-text-primary)',
            }}
            onFocus={e => { e.currentTarget.style.borderColor = 'var(--color-accent)' }}
            onBlur={e => { e.currentTarget.style.borderColor = 'var(--color-border)' }}
          />
        </div>
        <div className="relative">
          <Filter size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-text-muted)' }} />
          <select
            value={filterCompetency}
            onChange={e => setFilterCompetency(e.target.value)}
            className="pl-9 pr-4 py-2.5 rounded-lg text-sm outline-none appearance-none cursor-pointer transition-colors min-w-[200px]"
            style={{
              backgroundColor: 'var(--color-bg-card)',
              border: '1px solid var(--color-border)',
              color: 'var(--color-text-primary)',
            }}
            onFocus={e => { e.currentTarget.style.borderColor = 'var(--color-accent)' }}
            onBlur={e => { e.currentTarget.style.borderColor = 'var(--color-border)' }}
          >
            <option value="">{t('task.filterComp')}</option>
            {competencies.map(c => (
              <option key={c.id} value={c.id}>{c.name} ({c.name_cn})</option>
            ))}
          </select>
        </div>
      </div>

      {/* Task List */}
      {filtered.length === 0 ? (
        <div className="rounded-xl border p-12 text-center" style={{ backgroundColor: 'var(--color-bg-card)', borderColor: 'var(--color-border)' }}>
          <Puzzle size={32} style={{ color: 'var(--color-text-muted)', margin: '0 auto' }} />
          <p className="mt-3 text-sm" style={{ color: 'var(--color-text-muted)' }}>
            {searchQuery || filterCompetency ? 'No tasks match your filters.' : 'No tasks defined yet. Create your first assessment task.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(task => {
            const status = statusConfig[task.status] ?? statusConfig.draft
            const diffColor = getDifficultyColor(task.difficulty)
            return (
              <div
                key={task.id}
                className="rounded-xl border transition-colors flex flex-col"
                style={{ backgroundColor: 'var(--color-bg-card)', borderColor: 'var(--color-border)' }}
              >
                <div className="p-5 flex-1 flex flex-col">
                  {/* Header Row */}
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="text-base font-semibold leading-snug" style={{ color: 'var(--color-text-primary)' }}>{task.title}</h3>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => openEditModal(task)}
                        className="p-1.5 rounded-lg transition-colors"
                        style={{ color: 'var(--color-text-muted)' }}
                        onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)'; e.currentTarget.style.color = 'var(--color-accent)' }}
                        onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'var(--color-text-muted)' }}
                      >
                        <Edit3 size={14} />
                      </button>
                      <button
                        onClick={() => setConfirmDelete(task.id)}
                        className="p-1.5 rounded-lg transition-colors"
                        style={{ color: 'var(--color-text-muted)' }}
                        onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)'; e.currentTarget.style.color = 'var(--color-danger)' }}
                        onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'var(--color-text-muted)' }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>

                  {/* Badges */}
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: diffColor.bg, color: diffColor.text }}>
                      {task.difficulty}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: status.bg, color: status.text }}>
                      {status.label}
                    </span>
                  </div>

                  {/* Description */}
                  <p className="text-xs mb-3 line-clamp-2" style={{ color: 'var(--color-text-secondary)' }}>{task.description}</p>

                  {/* Competency */}
                  <div className="flex items-center gap-1.5 mb-3">
                    <Tag size={12} style={{ color: 'var(--color-text-muted)' }} />
                    <span className="text-xs truncate" style={{ color: 'var(--color-text-muted)' }}>{getCompetencyName(task.competency_id)}</span>
                  </div>

                  {/* Tools */}
                  <div className="mb-3">
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <Wrench size={12} style={{ color: 'var(--color-text-muted)' }} />
                      <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Tools</span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {task.tools_allowed.length === 0 ? (
                        <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>None</span>
                      ) : (
                        task.tools_allowed.map(tool => (
                          <span
                            key={tool}
                            className="text-xs px-1.5 py-0.5 rounded font-mono"
                            style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-secondary)' }}
                          >
                            {tool}
                          </span>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="mt-auto flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <ChevronRight size={12} style={{ color: 'var(--color-text-muted)' }} />
                      <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>{task.expected_steps} expected steps</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Clock size={12} style={{ color: 'var(--color-text-muted)' }} />
                      <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>{new Date(task.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-[8vh] px-4" onClick={() => setShowModal(false)}>
          <div className="fixed inset-0" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }} />
          <div
            className="relative w-full max-w-2xl rounded-xl border shadow-2xl max-h-[85vh] overflow-y-auto"
            style={{ backgroundColor: 'var(--color-bg-secondary)', borderColor: 'var(--color-border)' }}
            onClick={e => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'var(--color-border)' }}>
              <h2 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                {editingId ? 'Edit Task' : 'New Task'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-1.5 rounded-lg transition-colors"
                style={{ color: 'var(--color-text-secondary)' }}
                onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)' }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent' }}
              >
                <X size={18} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-5">
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>{t('task.taskTitle')} *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={e => setForm(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                  style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
                  placeholder="e.g. Market Entry Strategy Analysis"
                />
              </div>

              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>Description</label>
                <textarea
                  value={form.description}
                  onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 rounded-lg text-sm outline-none resize-none"
                  style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
                  placeholder="Describe the task scenario..."
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>{t('task.competency')} *</label>
                  <select
                    value={form.competency_id}
                    onChange={e => setForm(prev => ({ ...prev, competency_id: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg text-sm outline-none appearance-none cursor-pointer"
                    style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
                  >
                    <option value="">Select competency...</option>
                    {competencies.map(c => (
                      <option key={c.id} value={c.id}>{c.name} ({c.name_cn})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>{t('task.difficulty')}</label>
                  <select
                    value={form.difficulty}
                    onChange={e => setForm(prev => ({ ...prev, difficulty: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg text-sm outline-none appearance-none cursor-pointer"
                    style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
                  >
                    {difficultyOptions.map(d => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>Status</label>
                  <select
                    value={form.status}
                    onChange={e => setForm(prev => ({ ...prev, status: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg text-sm outline-none appearance-none cursor-pointer"
                    style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
                  >
                    <option value="draft">Draft</option>
                    <option value="reviewing">Reviewing</option>
                    <option value="approved">Approved</option>
                    <option value="published">Published</option>
                    <option value="deprecated">Deprecated</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>{t('task.expectedSteps')}</label>
                  <input
                    type="number"
                    value={form.expected_steps}
                    onChange={e => setForm(prev => ({ ...prev, expected_steps: Math.max(1, parseInt(e.target.value) || 1) }))}
                    min={1}
                    max={50}
                    className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                    style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>{t('task.rubricId')}</label>
                <input
                  type="text"
                  value={form.rubric_id}
                  onChange={e => setForm(prev => ({ ...prev, rubric_id: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                  style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
                  placeholder="Associated rubric identifier..."
                />
              </div>

              {/* Tools Allowed */}
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>{t('task.toolsAllowed')}</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={toolInput}
                    onChange={e => setToolInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTool() } }}
                    className="flex-1 px-3 py-2 rounded-lg text-sm outline-none"
                    style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
                    placeholder="Tool name, press Enter to add..."
                  />
                  <button
                    onClick={addTool}
                    className="px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                    style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-accent)', border: '1px solid var(--color-border)' }}
                  >
                    Add
                  </button>
                </div>
                {form.tools_allowed.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {form.tools_allowed.map(tool => (
                      <span
                        key={tool}
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded font-mono"
                        style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-secondary)' }}
                      >
                        {tool}
                        <button
                          onClick={() => removeTool(tool)}
                          style={{ color: 'var(--color-text-muted)' }}
                          onMouseEnter={e => { e.currentTarget.style.color = 'var(--color-danger)' }}
                          onMouseLeave={e => { e.currentTarget.style.color = 'var(--color-text-muted)' }}
                        >
                          <X size={12} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {submitError && (
                <div className="flex items-center gap-2 text-sm p-3 rounded-lg" style={{ backgroundColor: 'rgba(248,113,113,0.12)', color: 'var(--color-danger)' }}>
                  <AlertCircle size={14} />
                  {submitError}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t" style={{ borderColor: 'var(--color-border)' }}>
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                style={{ color: 'var(--color-text-secondary)' }}
                onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)' }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent' }}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                style={{ backgroundColor: 'var(--color-accent)', color: '#fff' }}
              >
                {submitting ? (
                  <Loader size={14} className="animate-spin" />
                ) : (
                  <Check size={14} />
                )}
                {editingId ? t('common.save') : t('common.create')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" onClick={() => setConfirmDelete(null)}>
          <div className="fixed inset-0" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }} />
          <div
            className="relative w-full max-w-sm rounded-xl border shadow-2xl p-6"
            style={{ backgroundColor: 'var(--color-bg-secondary)', borderColor: 'var(--color-border)' }}
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(248,113,113,0.12)' }}>
                <AlertCircle size={20} style={{ color: 'var(--color-danger)' }} />
              </div>
              <div>
                <h3 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>{t('common.delete')} {t('task.title')}</h3>
                <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-secondary)' }}>This action cannot be undone.</p>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmDelete(null)}
                className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                style={{ color: 'var(--color-text-secondary)' }}
                onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)' }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent' }}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => handleDelete(confirmDelete)}
                className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                style={{ backgroundColor: 'var(--color-danger)', color: '#fff' }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}